import type { StripeSubscriptionSummaryDto } from "@intelligems/ig-types";
import {
  isTrialDeadlineAhead,
  resolveTrialDeadline,
} from "@src/utils/trial-deadline";

export type TrialReminderEvent = {
  daysRemaining: number;
  modulesActive: string[];
  trialEndDate: string;
};

type TrialReminderSubscription = Pick<
  StripeSubscriptionSummaryDto,
  "trialEnd" | "stripeProductLinks"
>;

/**
 * The analytics payload for a trial reminder, or null when there is no live
 * deadline to report. A `trialing` subscription outlives its own deadline, so
 * the series would otherwise carry reminders for trials that had already
 * lapsed, counted as zero days alongside the genuine final-day ones.
 *
 * `daysRemaining` is the calendar-day count to `trialEnd` and reads 0 on the
 * final day, which is still live — the same quantity the merchant is shown.
 */
export const trialReminderEvent = (
  subscription: TrialReminderSubscription
): TrialReminderEvent | null => {
  const { trialEnd, stripeProductLinks } = subscription;
  const deadline = resolveTrialDeadline(trialEnd);

  if (trialEnd == null || !isTrialDeadlineAhead(deadline)) {
    return null;
  }

  return {
    daysRemaining: deadline.state === "running" ? deadline.remainingDays : 0,
    modulesActive: stripeProductLinks
      .map((link) => link.stripeProduct?.name)
      .filter((name): name is string => Boolean(name)),
    trialEndDate: new Date(trialEnd * 1_000).toISOString(),
  };
};
